// src/components/Home.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, PanInfo, Variants } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import { cs, enGB, de } from 'date-fns/locale';
import { useReservation } from '../hooks/useReservation';
import { Wifi, ParkingCircle, Flame, Trees, Utensils, Tv, Star } from 'lucide-react';

registerLocale('cs', cs);
registerLocale('en', enGB);
registerLocale('de', de);

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: (delay: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.6, ease: 'easeOut' },
  }),
};

const images = [
  '/images/gallery-01.webp',
  '/images/gallery-02.webp',
  '/images/gallery-03.webp',
  '/images/gallery-04.webp',
];

const variants: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
    position: 'absolute',
  }),
  center: {
    x: 0,
    opacity: 1,
    position: 'absolute',
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0,
    position: 'absolute',
  }),
};

const wrap = (min: number, max: number, v: number) => {
  const range = max - min;
  return ((((v - min) % range) + range) % range) + min;
};

const Home = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { blockedDates, formatLocalDate, MAX_PEOPLE } = useReservation();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);

  const blockedDatesList = Array.from(blockedDates).map(d => {
    const [year, month, day] = d.split('-').map(Number);
    return new Date(year, month - 1, day);
  });

  const handleQuickReserve = () => {
    void navigate('/rezervace', {
      state: {
        startDate,
        endDate,
        adults,
        children
      }
    });
  };

  const [[page, direction], setPage] = useState<[number, number]>([0, 0]);
  const imageIndex = wrap(0, images.length, page);
  const autoPlayInterval = 5000;
  const [progress, setProgress] = useState(0); // 0..1
  const rafRef = useRef<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const paginate = useCallback(
    (newDirection: number) => {
      setPage(([p]) => [p + newDirection, newDirection]);
    },
    []
  );

  // Autoplay logic with progress bar
  useEffect(() => {
    let start: number | null = null;
    const step = (timestamp: number) => {
      if (start === null) start = timestamp;
      const elapsed = timestamp - start;
      const ratio = Math.min(elapsed / autoPlayInterval, 1);
      setProgress(ratio);
      if (ratio >= 1) {
        paginate(1);
        start = timestamp;
      }
      if (!isHovered) {
        rafRef.current = window.requestAnimationFrame(step);
      }
    };
    if (!isHovered) {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      setProgress(0);
      rafRef.current = window.requestAnimationFrame(step);
    }
    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, [page, isHovered, paginate]);

  // Drag/swipe handler
  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const velocityThreshold = 500;
    const offsetThreshold = 100;
    if (info.offset.x > offsetThreshold || info.velocity.x > velocityThreshold) {
      paginate(-1);
    } else if (info.offset.x < -offsetThreshold || info.velocity.x < -velocityThreshold) {
      paginate(1);
    }
  };

  // Review Carousel Logic
  const reviews = [
    {
      name: 'Jarmila Havlová',
      date: 'Srpen 2025',
      rating: 5,
      text: 'Co říct, skvělé ubytování pro skupinu přátel i rodinu. Chalupa je prostorná, čistá a velmi dobře vybavená. V dešti jsme ocenili stolní fotbálek uvnitř.',
    },
    {
      name: 'Katarina Benova',
      date: 'Červen 2025',
      rating: 5,
      text: 'Chalupu jsme zažili v letním období mimo sezonu, takže všude byl klid a málo lidí. Chalupa nabízí soukromí, je v tiché lokalitě. Pěšky se dostanete do okolní přírody.',
    },
    {
      name: 'Veronika Průšková',
      date: 'Září 2025',
      rating: 5,
      text: 'Velice krásná a prostorná chalupa. Byli jsme moc spokojeni. Super vybavení, nové krásné postele, fotbálek, spousta společenských her. Venku velká oplocená zahrada.',
    },
    {
      name: 'Jan Cervenak',
      date: 'Květen 2025',
      rating: 5,
      text: 'Pobyt na Chalupě Laura byl naprosto skvělý. Krásné a čisté ubytování v klidném prostředí, ideální pro odpočinek. Chalupa je stylově zařízená, perfektně vybavená.',
    },
    {
      name: 'Gabriela Nesvadbová',
      date: 'Květen 2025',
      rating: 5,
      text: 'Velmi krásné a útulné ubytování. Stylově zařízené, čisté. Venkovní posezení, gril a ohniště. Nic nám nechybělo. Majitelé jsou příjemní mladí lidé.',
    },
    {
      name: 'Kuba (Syq)',
      date: 'Květen 2025',
      rating: 5,
      text: 'Lovely place, very nice people you have everything you need. View from the window is beautiful. Neibourhood is quiet.',
    },
  ];

  const [reviewIndex, setReviewIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setReviewIndex((prev) => (prev + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [reviews.length]);

  const visibleReviews = [
    reviews[reviewIndex],
    reviews[(reviewIndex + 1) % reviews.length],
    reviews[(reviewIndex + 2) % reviews.length],
  ];

  return (
    <div className="min-h-screen bg-[#f7f2ed] text-[#3f2b25]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 flex flex-col-reverse lg:flex-row items-center gap-12">
          {/* Text panel */}
          <motion.div
            className="w-full lg:w-1/2 bg-white/90 backdrop-blur-md rounded-2xl p-10 shadow-2xl flex flex-col gap-6"
            initial="hidden"
            animate="visible"
            custom={0}
            variants={fadeUp}
          >
            <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight">
              {t('home.welcome')} <span className="text-[#5a3e2b]">{t('home.tagline')}</span>
            </h1>
            <p className="text-lg font-medium leading-relaxed">
              {t('home.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-2">
              <Link
                to="/information"
                className="flex-1 inline-flex justify-center items-center gap-2 bg-white border border-[#4B2E2E] text-[#4B2E2E] px-6 py-3 rounded-xl font-medium shadow-sm hover:shadow-md transition"
              >
                {t('home.more')}
              </Link>
            </div>
          </motion.div>

          {/* Hero image (bez zesvětlení) */}
          <motion.div
            className="w-full lg:w-1/2"
            initial="hidden"
            animate="visible"
            custom={0.2}
            variants={fadeUp}
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-[#4B2E2E]/10">
              <img
                src="/images/gallery-01.webp"
                alt="Chalupa Laura – Hlavní fotografie"
                className="w-full h-auto object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Reservation Bar */}
      <section className="relative z-10 -mt-10 mb-16 px-6">
        <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-xl p-6 flex flex-col md:flex-row items-center gap-4 border border-[#e6dbd2]">
          {/* Date Range */}
          <div className="flex-1 w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservation.date')}</label>
            <div className="flex gap-2">
              <div className="w-1/2">
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  minDate={new Date()}
                  excludeDates={blockedDatesList}
                  dayClassName={date =>
                    blockedDates.has(formatLocalDate(date)) ? 'react-datepicker__day--excluded' : ''
                  }
                  placeholderText={t('reservation.checkIn')}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B2E2E] focus:border-transparent outline-none"
                  dateFormat="dd.MM.yyyy"
                  locale={i18n.language}
                  calendarClassName="premium-datepicker"
                />
              </div>
              <div className="w-1/2">
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate || new Date()}
                  excludeDates={blockedDatesList}
                  dayClassName={date =>
                    blockedDates.has(formatLocalDate(date)) ? 'react-datepicker__day--excluded' : ''
                  }
                  placeholderText={t('reservation.checkOut')}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B2E2E] focus:border-transparent outline-none"
                  dateFormat="dd.MM.yyyy"
                  locale={i18n.language}
                  calendarClassName="premium-datepicker"
                />
              </div>
            </div>
          </div>

          {/* Guests */}
          <div className="w-full md:w-auto flex gap-2">
            <div className="w-full md:w-32">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservation.adults')}</label>
              <select
                value={adults}
                onChange={(e) => setAdults(Number(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B2E2E] outline-none bg-white"
              >
                {Array.from({ length: Math.min(MAX_PEOPLE - children, MAX_PEOPLE) }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="w-full md:w-32">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservation.children')}</label>
              <select
                value={children}
                onChange={(e) => setChildren(Number(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B2E2E] outline-none bg-white"
              >
                {Array.from({ length: MAX_PEOPLE - adults + 1 }, (_, i) => i).map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Button */}
          <div className="w-full md:w-auto mt-4 md:mt-0 flex items-end">
            <button
              onClick={handleQuickReserve}
              className="w-full md:w-auto bg-[#4B2E2E] text-white px-8 py-3.5 rounded-xl font-bold shadow-lg hover:scale-105 transition flex items-center justify-center gap-2 h-[50px]"
            >
              {t('nav.reservation')}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
            </button>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="max-w-7xl mx-auto px-6 mb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-[#3f2b25] mb-4">{t('home.amenitiesTitle', 'Co u nás najdete')}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">{t('home.amenitiesSubtitle', 'Vše pro váš dokonalý komfort a odpočinek v srdci přírody.')}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {[
            { icon: <Wifi className="w-8 h-8" />, labelKey: 'home.amenity.wifi' },
            { icon: <ParkingCircle className="w-8 h-8" />, labelKey: 'home.amenity.parking' },
            { icon: <Flame className="w-8 h-8" />, labelKey: 'home.amenity.fireplace' },
            { icon: <Trees className="w-8 h-8" />, labelKey: 'home.amenity.garden' },
            { icon: <Utensils className="w-8 h-8" />, labelKey: 'home.amenity.kitchen' },
            { icon: <Tv className="w-8 h-8" />, labelKey: 'home.amenity.tv' },
          ].map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -5 }}
              className="flex flex-col items-center gap-3 p-4 rounded-xl bg-white shadow-sm hover:shadow-md transition border border-[#e6dbd2]"
            >
              <div className="text-[#4B2E2E] bg-[#f7f2ed] p-3 rounded-full">
                {item.icon}
              </div>
              <span className="font-medium text-center text-gray-800">{t(item.labelKey)}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Reviews Section */}
      <section className="bg-white py-20 mb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#3f2b25] mb-4">{t('home.reviewsTitle', 'Co o nás říkají hosté')}</h2>
            <div className="flex justify-center gap-1 text-yellow-400 mb-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-current" />
              ))}
            </div>
            <p className="text-gray-600">Průměrné hodnocení 5/5</p>
          </div>

          <div className="relative overflow-hidden h-auto min-h-[300px]">
            <AnimatePresence mode='popLayout'>
              <motion.div
                key={reviewIndex}
                className="grid md:grid-cols-3 gap-8 w-full"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
              >
                {visibleReviews.map((review, index) => (
                  <div
                    key={`${review.name}-${index}`}
                    className="bg-[#f7f2ed] p-8 rounded-2xl relative h-full flex flex-col"
                  >
                    <div className="absolute -top-4 left-8 text-[#4B2E2E] text-6xl opacity-20 font-serif">"</div>
                    <div className="flex gap-1 text-yellow-500 mb-4 relative z-10">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-current" />
                      ))}
                    </div>
                    <p className="text-gray-700 italic mb-6 relative z-10 flex-grow">{review.text}</p>
                    <div className="flex items-center gap-4 mt-auto">
                      <div className="w-10 h-10 bg-[#4B2E2E] rounded-full flex items-center justify-center text-white font-bold shrink-0">
                        {review.name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="font-bold text-[#3f2b25]">{review.name}</h4>
                        <span className="text-sm text-gray-500">{review.date}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Carousel preview galerie */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold">{t('home.galleryPreview')}</h2>
        </div>
        <div
          className="relative select-none"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Slides container */}
          <div className="relative overflow-hidden rounded-3xl h-[380px]">
            <AnimatePresence initial={false} custom={direction}>
              <motion.img
                key={page}
                src={images[imageIndex]}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                alt={`Galerie ${imageIndex + 1}`}
                loading="lazy"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.25}
                onDragEnd={handleDragEnd}
                whileTap={{ cursor: 'grabbing' }}
              />
            </AnimatePresence>

            {/* gradient overlay for depth */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/15 to-transparent rounded-3xl" />
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-1 bg-[#e6dbd2] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#4B2E2E] transition-all"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>

          {/* Prev/Next */}
          <button
            aria-label="Předchozí fotografie"
            onClick={() => paginate(-1)}
            className="absolute top-1/2 left-3 -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg hover:scale-105 transition flex items-center justify-center"
          >
            <svg className="h-5 w-5 text-[#4B2E2E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            aria-label="Další fotografie"
            onClick={() => paginate(1)}
            className="absolute top-1/2 right-3 -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg hover:scale-105 transition flex items-center justify-center"
          >
            <svg className="h-5 w-5 text-[#4B2E2E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Indicators */}
          <div className="mt-6 flex justify-center gap-3">
            {images.map((_, idx) => {
              const isActive = idx === imageIndex;
              const newDirection = isActive ? 0 : idx > imageIndex ? 1 : -1;
              return (
                <button
                  key={idx}
                  onClick={() => setPage([idx, newDirection])}
                  aria-label={`Slide ${idx + 1}`}
                  className={`
                    h-3 w-3 rounded-full transition-transform
                    ${isActive ? 'bg-[#4B2E2E] scale-125' : 'bg-[#C4B9B0]/60'}
                  `}
                />
              );
            })}
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/galerie"
              className="inline-block bg-[#4B2E2E] text-white px-10 py-3 rounded-full font-semibold hover:brightness-105 transition"
            >
              {t('home.fullGallery')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
